import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://ptzgtsedmunaldnaiwag.supabase.co'
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InB0emd0c2VkbXVuYWxkbmFpd2FnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjE2MjAzMTksImV4cCI6MjA3NzE5NjMxOX0.4N3xXtgeC4EHO5XMcfXsf6ETaUiNs3jvlvIgXVxa_4g'

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
})

// Log Supabase connection status
console.log('✅ Supabase configured:', { supabaseUrl })

// OAuth helpers
export const signInWithGoogle = async () => {
  console.log('🌐 Initiating Google OAuth...')
  
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        }
      }
    })
    
    console.log('✅ signInWithOAuth 调用完成:', { data, error })
    
    if (error) {
      console.error('❌ Google OAuth error:', error)
      throw error
    }
    
    // 注意: OAuth会立即跳转，这里的代码不会执行
    console.log('✅ Google OAuth initiated:', data)
    
    return { data, error }
  } catch (error) {
    console.error('❌ signInWithOAuth 异常:', error)
    throw error
  }
}

export const signInWithFacebook = async () => {
  console.log('🌐 Initiating Facebook OAuth...')
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'facebook',
    options: {
      redirectTo: `${window.location.origin}/auth/callback`
    }
  })
  
  if (error) {
    console.error('❌ Facebook OAuth error:', error)
    alert(`登录失败: ${error.message}`)
  } else {
    console.log('✅ Facebook OAuth initiated:', data)
  }
  
  return { data, error }
}

// Auth helpers
export const signUp = async (email, password, name) => {
  console.log('📝 Signing up:', { email, name })
  
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name: name || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    })
    
    console.log('✅ Signup result:', { data, error })
    
    // 检查是否是重复注册
    if (error && error.message.includes('already registered')) {
      console.warn('⚠️ User already exists')
      return { 
        data: null, 
        error: { 
          message: '该邮箱已被注册，请直接登录或使用"忘记密码"功能' 
        } 
      }
    }
    
    return { data, error }
  } catch (error) {
    console.error('❌ Signup exception:', error)
    return { data: null, error }
  }
}

export const signIn = async (email, password) => {
  console.log('🔐 Signing in:', { email })
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  })
  return { data, error }
}

export const signOut = async () => {
  console.log('🚪 Signing out')
  const { error } = await supabase.auth.signOut()
  return { error }
}

export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser()
  return user
}

export const getSession = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  return session
}

// TODO: 后续接入后端功能时取消注释
// Database helpers
export const getUserProfile = async (userId) => {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .single()
  
  return { data, error }
}

export const updateUserProfile = async (userId, updates) => {
  const { data, error } = await supabase
    .from('users')
    .update(updates)
    .eq('id', userId)
    .select()
    .single()
  
  return { data, error }
}

export const createFileRecord = async (fileData) => {
  const { data, error } = await supabase
    .from('files')
    .insert(fileData)
    .select()
    .single()
  
  return { data, error }
}

export const getFiles = async (userId) => {
  const { data, error } = await supabase
    .from('files')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
  
  return { data, error }
}

export const createConversion = async (conversionData) => {
  const { data, error } = await supabase
    .from('conversions')
    .insert(conversionData)
    .select()
    .single()
  
  return { data, error }
}

export const getConversions = async (userId) => {
  const { data, error } = await supabase
    .from('conversions')
    .select(`
      *,
      source_file:files!source_file_id(*),
      target_file:files!target_file_id(*)
    `)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
  
  return { data, error }
}

export const updateConversion = async (conversionId, updates) => {
  const { data, error } = await supabase
    .from('conversions')
    .update(updates)
    .eq('id', conversionId)
    .select()
    .single()
  
  return { data, error }
}

export const createSubscription = async (subscriptionData) => {
  const { data, error } = await supabase
    .from('subscriptions')
    .insert(subscriptionData)
    .select()
    .single()
  
  return { data, error }
}

export const getSubscription = async (userId) => {
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .eq('user_id', userId)
    .eq('status', 'active')
    .single()
  
  return { data, error }
}

export const updateUsage = async (userId, usage) => {
  const { data, error } = await supabase
    .from('users')
    .update({ usage })
    .eq('id', userId)
    .select()
    .single()
  
  return { data, error }
}

export default supabase
